/**
 * Diagrama de Empalmes/Fusiones de Fibras
 * Visualiza conexiones hilo a hilo entre cables en un nodo
 * @module components/diagrams/EmpalmesDiagram
 */

import { useMemo } from 'react'
import { useFiberStore, useNodeStore } from '@/stores'
import { cableTouchesNode, cableEndpointAtNode } from '@/utils/cableEndpoints'
import { Cable, GitBranch, ArrowRight, Circle, Layers, Zap } from 'lucide-react'

const COLORES_TIA598 = {
  azul: '#0066CC',
  naranja: '#FF6600',
  verde: '#00AA00',
  marron: '#8B4513',
  gris: '#808080',
  blanco: '#F5F5F5',
  rojo: '#FF0000',
  negro: '#333333',
  amarillo: '#FFCC00',
  violeta: '#9900CC',
  rosa: '#FF66CC',
  turquesa: '#00CCCC',
}

interface Empalme {
  fibraOrigenId: string
  fibraDestinoId: string
  cableOrigenId: string
  cableDestinoId: string
  cableOrigenNombre: string
  cableDestinoNombre: string
  bufferOrigen?: string
  bufferDestino?: string
  colorOrigen: string
  colorDestino: string
  hiloOrigen: number
  hiloDestino: number
  tipo: 'fusion' | 'derivacion' | 'splitter'
  /** Para tipo 'splitter': de qué lado está el splitter en esta fila. */
  ladoSplitter?: 'entrada' | 'salida'
}

interface EmpalmesDiagramProps {
  nodeId: string
  compact?: boolean
  /** Resaltar filas cuyo hilo coincida (p. ej. iluminación desde cable o trazado) */
  highlightedFibraIds?: string[]
}

export function EmpalmesDiagram({ nodeId, compact = false, highlightedFibraIds }: EmpalmesDiagramProps) {
  const cables = useFiberStore((state) => state.cables)
  const nodes = useNodeStore((state) => state.nodes)
  
  const node = nodes.find(n => n.id === nodeId)
  
  // Obtener cables conectados a este nodo
  const cablesConectados = useMemo(() => {
    return cables.filter((c) => cableTouchesNode(c as any, nodeId))
  }, [cables, nodeId])
  
  // Analizar empalmes/conexiones entre fibras
  const empalmes = useMemo(() => {
    const conexiones: Empalme[] = []
    
    cablesConectados.forEach(cable => {
      const cableId = cable.id
      const cableNombre = (cable as any).nombre || (cable as any).codigo || `Cable ${cable.tipo}`

      // Un cable tiene dos puntas y cada una se empalma de forma independiente
      // -- hay que leer el estado de la punta que realmente cae en este nodo,
      // no un agregado global (ver src/types/fibers.ts: Fibra.extremos).
      const extremo = cableEndpointAtNode(cable as any, nodeId)
      if (!extremo) return

      const procesarFibra = (fibra: any, bufferColor?: string) => {
        const estadoExtremo = fibra.extremos?.[extremo]
        const conectadoA: string | null = estadoExtremo ? estadoExtremo.conectadoA : fibra.conectadaA
        const estadoFibra: string = estadoExtremo ? estadoExtremo.estado : fibra.estado
        if (!conectadoA) return

        const parts = conectadoA.split('|')
        if (parts.length < 3) return

        const [cableDestinoIdPart, bufferDest, colorDest] = parts

        // Buscar el cable destino
        const cableDestino = cables.find(c =>
          c.id === cableDestinoIdPart ||
          c.id.includes(cableDestinoIdPart) ||
          cableDestinoIdPart.includes(c.id)
        )

        if (cableDestino) {
          conexiones.push({
            fibraOrigenId: fibra.fibraId,
            fibraDestinoId: conectadoA,
            cableOrigenId: cableId,
            cableDestinoId: cableDestino.id,
            cableOrigenNombre: cableNombre,
            cableDestinoNombre: (cableDestino as any).nombre || (cableDestino as any).codigo || `Cable ${cableDestino.tipo}`,
            bufferOrigen: bufferColor,
            bufferDestino: bufferDest === 'SB' ? undefined : bufferDest,
            colorOrigen: fibra.color,
            colorDestino: colorDest,
            hiloOrigen: fibra.numero,
            hiloDestino: parseInt(colorDest) || fibra.numero,
            tipo: estadoFibra === 'derivado' ? 'derivacion' : 'fusion'
          })
        }
      }

      // Procesar fibras del cable
      if (cable.tieneBuffers && cable.buffers) {
        cable.buffers.forEach((buffer: any) => {
          buffer.fibras?.forEach((fibra: any) => procesarFibra(fibra, buffer.color))
        })
      } else if (cable.fibras) {
        cable.fibras.forEach((fibra: any) => procesarFibra(fibra))
      }
    })
    
    return conexiones
  }, [cablesConectados, cables, nodeId])
  
  // También buscar conexiones desde la configuración del nodo (splitters)
  const conexionesSplitter = useMemo(() => {
    const nodeConfig = (node as any)?.config
    if (!nodeConfig?.splitters) return []
    
    const splitterConexiones: Empalme[] = []
    
    nodeConfig.splitters.forEach((splitter: any) => {
      // Conexión de entrada del splitter
      if (splitter.entrada?.cableId) {
        const cableEntrada = cables.find(c => c.id === splitter.entrada.cableId)
        if (cableEntrada) {
          splitterConexiones.push({
            fibraOrigenId: splitter.entrada.fibraId || '',
            fibraDestinoId: `splitter_${splitter.id}_in`,
            cableOrigenId: splitter.entrada.cableId,
            cableDestinoId: 'splitter',
            cableOrigenNombre: (cableEntrada as any).nombre || `Cable ${cableEntrada.tipo}`,
            cableDestinoNombre: `Splitter ${splitter.tipo || '1:8'}`,
            bufferOrigen: splitter.entrada.bufferColor,
            colorOrigen: splitter.entrada.fibraColor || 'azul',
            colorDestino: 'entrada',
            hiloOrigen: splitter.entrada.fibraNum || 1,
            hiloDestino: 0,
            tipo: 'splitter',
            ladoSplitter: 'entrada'
          })
        }
      }

      // Salidas del splitter que van directo a un cable (cascada o salida
      // local) -- antes no se mostraban, solo la entrada.
      splitter.salidas?.forEach((salida: any) => {
        const destino = salida?.destino
        if (destino?.tipo !== 'cable' || !destino.cableId) return
        const cableSalida = cables.find(c => c.id === destino.cableId)
        if (!cableSalida) return
        splitterConexiones.push({
          fibraOrigenId: `splitter_${splitter.id}_out${salida.puerto}`,
          fibraDestinoId: destino.fibraId || '',
          cableOrigenId: 'splitter',
          cableDestinoId: destino.cableId,
          cableOrigenNombre: `Splitter ${splitter.tipo || '1:8'} (puerto ${salida.puerto})`,
          cableDestinoNombre: destino.cableNombre || (cableSalida as any).nombre || (cableSalida as any).codigo || `Cable ${cableSalida.tipo}`,
          colorOrigen: 'salida',
          colorDestino: destino.fibraColor || 'azul',
          bufferDestino: destino.bufferNum != null ? String(destino.bufferNum) : undefined,
          hiloOrigen: 0,
          hiloDestino: destino.fibraNum || 1,
          tipo: 'splitter',
          ladoSplitter: 'salida'
        })
      })
    })
    
    return splitterConexiones
  }, [node, cables])
  
  const todosEmpalmes = [...empalmes, ...conexionesSplitter]

  // Agrupar por cable de origen (hooks antes de cualquier return condicional)
  const empalmesPorCable = useMemo(() => {
    const grupos: Record<string, Empalme[]> = {}
    
    todosEmpalmes.forEach(emp => {
      // Para salidas de splitter, cableOrigenId es un placeholder ('splitter')
      // sin ficha real -- agrupar por el cable de verdad (el destino).
      const grupoId = emp.cableOrigenId === 'splitter' ? emp.cableDestinoId : emp.cableOrigenId
      if (!grupos[grupoId]) {
        grupos[grupoId] = []
      }
      grupos[grupoId].push(emp)
    })
    
    return grupos
  }, [todosEmpalmes])
  
  const isFibraHighlighted = (emp: Empalme) => {
    if (!highlightedFibraIds?.length) return false
    return highlightedFibraIds.some(
      (id) =>
        id &&
        (id === emp.fibraOrigenId ||
          id === emp.fibraDestinoId ||
          emp.fibraOrigenId?.includes(id) ||
          emp.fibraDestinoId?.includes(id))
    )
  }

  const handleExportPdf = async () => {
    const nodeName = (node as any)?.nombre || nodeId.slice(-8)
    const { exportSplicesDiagramPDF } = await import('@/utils/diagramPdfExport')
    exportSplicesDiagramPDF(
      nodeName,
      todosEmpalmes.map((e) => ({
        cableEntrada: e.cableOrigenNombre,
        hiloEntrada: `${e.colorOrigen} H${e.hiloOrigen}`,
        cableSalida: e.cableDestinoNombre,
        hiloSalida: e.tipo === 'splitter' ? 'Splitter IN' : `${e.colorDestino} H${e.hiloDestino}`,
        tipo: e.tipo,
      }))
    )
  }

  if (todosEmpalmes.length === 0) {
    if (compact) return null

    return (
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4 text-center">
        <Layers className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">No hay empalmes configurados en este nodo</p>
        <p className="text-xs text-gray-400 mt-1">
          {cablesConectados.length} cable(s) conectado(s)
        </p>
      </div>
    )
  }

  if (compact) {
    return (
      <div className="text-xs space-y-1">
        <div className="flex items-center gap-2 text-gray-500">
          <GitBranch className="w-3 h-3" />
          <span>{todosEmpalmes.length} empalmes en este nodo</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {todosEmpalmes.slice(0, 3).map((emp, idx) => (
            <span
              key={idx}
              className={`px-1.5 py-0.5 rounded text-[10px] ${
                isFibraHighlighted(emp)
                  ? 'bg-yellow-200 dark:bg-yellow-800/50 text-yellow-900 dark:text-yellow-100 ring-2 ring-yellow-400'
                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              }`}
            >
              H{emp.hiloOrigen} → H{emp.hiloDestino}
            </span>
          ))}
          {todosEmpalmes.length > 3 && (
            <span className="text-gray-400">+{todosEmpalmes.length - 3} más</span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold">Diagrama de Empalmes</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{todosEmpalmes.length} conexiones</span>
          <button
            type="button"
            onClick={handleExportPdf}
            className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-dark-500 hover:bg-gray-100 dark:hover:bg-dark-600"
          >
            PDF
          </button>
        </div>
      </div>
      
      {/* Tabla de empalmes */}
      <div className="border rounded-lg overflow-hidden dark:border-dark-600">
        <table className="w-full text-xs">
          <thead className="bg-gray-100 dark:bg-dark-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                Cable Entrada
              </th>
              <th className="px-2 py-2 text-center font-medium text-gray-600 dark:text-gray-300">
                Hilo
              </th>
              <th className="px-2 py-2 text-center">
                <ArrowRight className="w-4 h-4 mx-auto text-gray-400" />
              </th>
              <th className="px-2 py-2 text-center font-medium text-gray-600 dark:text-gray-300">
                Hilo
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                Cable Salida
              </th>
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-dark-600">
            {todosEmpalmes.map((emp, idx) => (
              <tr
                key={idx}
                className={`hover:bg-gray-50 dark:hover:bg-dark-700 ${
                  isFibraHighlighted(emp)
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 ring-1 ring-inset ring-yellow-400'
                    : ''
                }`}
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {emp.ladoSplitter === 'salida' ? (
                      <Zap className="w-3 h-3 text-purple-500" />
                    ) : (
                      <Cable className="w-3 h-3 text-gray-400" />
                    )}
                    <div>
                      <div className="font-medium truncate max-w-[100px]" title={emp.cableOrigenNombre}>
                        {emp.cableOrigenNombre}
                      </div>
                      {emp.bufferOrigen && (
                        <div className="text-[10px] text-gray-400">Buffer {emp.bufferOrigen}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-center">
                  {emp.ladoSplitter === 'salida' ? (
                    <span className="text-purple-600 font-medium">OUT</span>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <Circle
                        className="w-3 h-3"
                        fill={COLORES_TIA598[emp.colorOrigen as keyof typeof COLORES_TIA598] || '#999'}
                        stroke={COLORES_TIA598[emp.colorOrigen as keyof typeof COLORES_TIA598] || '#999'}
                      />
                      <span className="font-mono">H{emp.hiloOrigen}</span>
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-center" title={
                  emp.tipo === 'splitter' ? 'Derivación a splitter' : 
                  emp.tipo === 'derivacion' ? 'Derivación' : 
                  'Fusión directa'
                }>
                  {emp.tipo === 'splitter' ? (
                    <Zap className="w-4 h-4 mx-auto text-purple-500" />
                  ) : emp.tipo === 'derivacion' ? (
                    <GitBranch className="w-4 h-4 mx-auto text-orange-500" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mx-auto text-blue-500" />
                  )}
                </td>
                <td className="px-2 py-2 text-center">
                  {emp.ladoSplitter === 'entrada' ? (
                    <span className="text-purple-600 font-medium">IN</span>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <Circle
                        className="w-3 h-3"
                        fill={COLORES_TIA598[emp.colorDestino as keyof typeof COLORES_TIA598] || '#999'}
                        stroke={COLORES_TIA598[emp.colorDestino as keyof typeof COLORES_TIA598] || '#999'}
                      />
                      <span className="font-mono">H{emp.hiloDestino}</span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {emp.ladoSplitter === 'entrada' ? (
                      <Zap className="w-3 h-3 text-purple-500" />
                    ) : (
                      <Cable className="w-3 h-3 text-gray-400" />
                    )}
                    <div>
                      <div className="font-medium truncate max-w-[100px]" title={emp.cableDestinoNombre}>
                        {emp.cableDestinoNombre}
                      </div>
                      {emp.bufferDestino && (
                        <div className="text-[10px] text-gray-400">Buffer {emp.bufferDestino}</div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Leyenda */}
      <div className="flex items-center gap-4 text-[10px] text-gray-500 border-t pt-3 dark:border-dark-600">
        <div className="flex items-center gap-1">
          <ArrowRight className="w-3 h-3 text-blue-500" />
          <span>Fusión directa</span>
        </div>
        <div className="flex items-center gap-1">
          <GitBranch className="w-3 h-3 text-orange-500" />
          <span>Derivación</span>
        </div>
        <div className="flex items-center gap-1">
          <Zap className="w-3 h-3 text-purple-500" />
          <span>Splitter</span>
        </div>
      </div>
      
      {/* Resumen por cable */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
        <h4 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
          Resumen por Cable
        </h4>
        <div className="space-y-2">
          {Object.entries(empalmesPorCable).map(([cableId, emps]) => {
            const cable = cables.find(c => c.id === cableId)
            const nombreCable = (cable as any)?.nombre || (cable as any)?.codigo || `Cable ${cable?.tipo || 'N/A'}`
            
            return (
              <div key={cableId} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <Cable className="w-3 h-3 text-gray-400" />
                  <span className="truncate max-w-[150px]">{nombreCable}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{emps.length} empalme(s)</span>
                  <div className="flex gap-1">
                    {emps.slice(0, 5).map((e, i) => {
                      // Para salidas de splitter el color real de la fibra de
                      // este cable está en colorDestino, no en colorOrigen
                      // (que es el placeholder 'salida' del lado splitter).
                      const color = e.ladoSplitter === 'salida' ? e.colorDestino : e.colorOrigen
                      return (
                        <Circle
                          key={i}
                          className="w-2 h-2"
                          fill={COLORES_TIA598[color as keyof typeof COLORES_TIA598] || '#999'}
                          stroke={COLORES_TIA598[color as keyof typeof COLORES_TIA598] || '#999'}
                        />
                      )
                    })}
                    {emps.length > 5 && <span className="text-gray-400">+{emps.length - 5}</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default EmpalmesDiagram
